import { z } from 'zod/v4';

/**
 * Full-fidelity company shape matching the legacy `api-service`
 * `companies_view`/`buildCompany` output. Kept intentionally loose — this
 * mirrors a ~40-column legacy view and downstream consumers (the frontend's
 * global auth store) read arbitrary fields from it.
 *
 * Everything but `id`/`officialName`/`name`/`country` is `.nullish()`
 * (accepts `null` *and* `undefined`), not just `.nullable()`. Two real
 * sources produce a sparse object here: a company profile that genuinely
 * hasn't filled in every field (SQL `NULL` columns), and — more
 * surprisingly — the minimal `ctx.company` shape the auth handler builds
 * (`backend/src/api/auth/authenticate-queries.ts`'s
 * `fetchCompanyById`, only `id`/`officialName`/`country`/`name`/
 * `facilityProcesses`/`isClientCompany`), which `UserDetailService.getById` passes straight
 * through as `company` when `isSwitchingCompany` is set. That
 * object's other ~35 keys are simply absent (`undefined`), which
 * `.nullable()` alone does not accept.
 */
export const FullCompanySchema = z.looseObject({
  id: z.string(),
  officialName: z.string(),
  name: z.string(),
  transliteratedName: z.string().nullish(),
  transliteratedOfficialName: z.string().nullish(),
  description: z.string().nullish(),
  country: z.string(),
  countryName: z.string().nullish(),
  county: z.string().nullish(),
  city: z.string().nullish(),
  zipCode: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  phone: z.string().nullish(),
  website: z.string().nullish(),
  companySize: z.string().nullish(),
  companyType: z.string().nullish(),
  featuredImageFileId: z.string().nullish(),
  featuredImageFileUrl: z.string().nullish(),
  originalFeaturedImageFileUrl: z.string().nullish(),
  videoFileId: z.string().nullish(),
  videoFileUrl: z.string().nullish(),
  originalVideoFileUrl: z.string().nullish(),
  numOfPermanentEmployees: z.number().nullish(),
  numOfMaleEmployees: z.number().nullish(),
  numOfFemaleEmployees: z.number().nullish(),
  facilityProcesses: z.string().nullish(),
  geojson: z.string().nullish(),
  imageList: z.array(z.record(z.string(), z.unknown())).nullish(),
  workosOrganizationId: z.string().nullish(),
  higgId: z.string().nullish(),
  globalLocationNumber: z.string().nullish(),
  vatId: z.string().nullish(),
  dunsNumber: z.string().nullish(),
  commercialRegisterNumber: z.string().nullish(),
  osHubId: z.string().nullish(),
  annualProductionCapacity: z.number().nullish(),
  annualProductionCapacityUnit: z.string().nullish(),
  minimumOrderQuantity: z.number().nullish(),
  minimumOrderQuantityPerStyle: z.number().nullish(),
  standardLeadTime: z.number().nullish(),
  shopId: z.string().nullish(),
  isDemoCompany: z.number().nullish(),
  isClientCompany: z.number().nullish(),
});

export type FullCompanyRow = z.infer<typeof FullCompanySchema>;
